import { sortBy } from 'es-toolkit'
import { encode } from 'iconv-lite'
import { useState, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button } from 'reactstrap'

import { CONNECTION_TYPES } from 'api/schedule_types/constants'

import { selectScheduleTypesStatus } from 'slices/scheduleTypesSlice'
import { exportData, ENABLE_DIALOG_ERROR_STATUS_CODES } from 'slices/utils'
import { selectWorkResultsStatus, clearErrorMessage, csvImportWorkResults } from 'slices/workResultsSlice'
import { selectWorkspacesStatus } from 'slices/workspacesSlice'

import { CustomModal, FileInput } from 'components/common'
import type { CharacterCodeType } from 'components/common/CharacterCodeSelect/CharacterCodeSelect'
import { CHARACTER_CODE_TYPES, CharacterCodeSelect } from 'components/common/CharacterCodeSelect/CharacterCodeSelect'

import useBusinessTime from 'hooks/useBusinessTime'
import useSubmitResult from 'hooks/useSubmitResult'

const FORMAT_FILE_NAME = '実績一括入力シート'

type Props = {
  isOpen: boolean
  onSuccess: () => void
  onCancel: () => void
  date: string // yyyy-MM-DD
}

const CSVManualRecordsInputDialog = ({ isOpen, onSuccess, onCancel, date }: Props) => {
  const [file, setFile] = useState<File | null>(null)
  const [characterCode, setCharacterCode] = useState<CharacterCodeType>(CHARACTER_CODE_TYPES.SHIFT_JIS)
  const [modalErrorMessage, setModalErrorMessage] = useState<string>()
  const { isRequesting, errorMessage } = useSelector(selectWorkResultsStatus, shallowEqual)
  const { partialWorkspaces } = useSelector(selectWorkspacesStatus, shallowEqual)
  const { allScheduleTypes } = useSelector(selectScheduleTypesStatus, shallowEqual)
  const { businessHourBlocks } = useBusinessTime()
  const dispatch = useDispatch()

  const { handleSubmit } = useSubmitResult({
    isRequesting,
    errorMessage,
    onSuccess: () => {
      setModalErrorMessage(undefined)
      onSuccess()
    },
    onError: () => {
      if (ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
        // ENABLE_DIALOGのときにはエラーダイアログが出るのでNotificationは出さない
        setModalErrorMessage(undefined)
      } else {
        setModalErrorMessage('保存できませんでした。')
      }
      dispatch(clearErrorMessage())
    },
  })

  const handleCancel = () => {
    setCharacterCode(CHARACTER_CODE_TYPES.SHIFT_JIS)
    setFile(null)
    setModalErrorMessage(undefined)
    onCancel()
  }

  const handleDownloadFormat = () => {
    const header = ['ワークスペース', '作業', ...businessHourBlocks.map(b => `${b.toString().padStart(2, '0')}:00`)]
    const allScheduleTypesWithWorkspaceName = allScheduleTypes
      .filter(s => s.connectionType === CONNECTION_TYPES.MANUAL)
      .map(s => {
        const workspace = partialWorkspaces.find(w => w.id === s.workspaceId)
        return {
          ...s,
          workspaceName: workspace?.name || '',
        }
      })

    const scheduleTypeRows = sortBy(allScheduleTypesWithWorkspaceName, ['workspaceName', 'name']).map(s => [
      s.workspaceName,
      s.name,
      ...businessHourBlocks.map(() => ''),
    ])

    const csvData = [header, ...scheduleTypeRows].map(r => r.join(',')).join('\n')
    const csvDataShiftJIS = encode(csvData, 'Shift_JIS')
    const blob = new Blob([csvDataShiftJIS], { type: 'text/csv' })
    exportData(blob, `${FORMAT_FILE_NAME}.csv`)
  }

  const handleApprove = () => {
    if (!file) {
      return
    }

    const formData = new FormData()
    formData.append('fileData', file)
    formData.append('encoding', characterCode)
    dispatch(csvImportWorkResults(date, formData))
    handleSubmit()
  }

  const disabled = useMemo(() => !(file && /\.csv$/i.test(file.name)), [file])

  const handleCharacterCodeChange = (value: CharacterCodeType) => {
    setCharacterCode(value)
  }

  return (
    <CustomModal
      isOpen={isOpen}
      title={`実績CSV一括入力 : ${date.replace(/-/g, '/')}`}
      onCancel={handleCancel}
      onApprove={handleApprove}
      approveDisabled={disabled}
      errorMessage={modalErrorMessage}
      onHideNotification={() => setModalErrorMessage(undefined)}
    >
      <div className="mb-3">
        {`下記${FORMAT_FILE_NAME}をダウンロードして、同じフォーマットでCSVアップロードをしてください。`}
      </div>
      <div className="mb-3">
        <CharacterCodeSelect characterCode={characterCode} onChange={handleCharacterCodeChange} />
      </div>
      <ul>
        <li>対象の作業は実績入力方法が｢手動アップロード｣の作業のみです。</li>
        <li>
          1列目にはワークスペース名、2列目には作業名を入れてください。登録されているワークスペース名、作業名と完全一致することで、実績の一括入力が可能です。
        </li>
      </ul>
      <Button color="link" className="ps-0 mb-3 shadow-none text-decoration-none" onClick={handleDownloadFormat}>
        {`${FORMAT_FILE_NAME}をダウンロード`}
      </Button>
      <FileInput id="memberFile" accept=".csv" onChange={f => setFile(f)} />
    </CustomModal>
  )
}

export default CSVManualRecordsInputDialog
